/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import IonTooltip from '../../components/tooltip/Tooltip';
import {useMedia} from '../../hooks/useMedia';
import {useToggleToolTip} from '../../hooks/useToggleToolTip';
import Hidden from '../../layout/Hidden';
import useGlobalProps from '../../props/useGlobalProps';
import {dlsAlt, dlsColor, dlsLayout, dlsZIndex} from '../../theme/theme';
import {SideSheetOrigin} from '../../types/types';
import ClickAwayListener from '../listeners/ClickAwayListener';
import SideSheetArrow from '../sidesheet/SideSheetArrow';
import Tooltip, {useNavigationStyle} from '../tooltip/Tooltip';
import ExpandCollapseToggle from './ExpandCollapseToggle';
import {NavigationTypes} from './Navigation';
import {PrimaryText} from './PrimaryText';
import SecondaryNavigation from './SecondaryNavigation';
import SecondaryNavigationItems from './SecondaryNavigationItems';
import navStyles from './Navigation.module.scss';

// defines the props for navigation item(s)
export interface NavigationItemTypes extends NavigationTypes, StylesProps {
  isSideSheetOpen: boolean;
  sideSheetOrigin?: SideSheetOrigin;
  isInitialized?: Boolean;
  isLoaded?: Boolean;
  currentPage: string;
  secondaryNavOpen?: boolean;
  showSecondaryNavigation?: boolean;
  closeSecondaryNavigation?: Function;
  overrideStyle?: GenericObject;
  expandClasses?: GenericObject;
  collapsed?: boolean;
  onHandleCollapse?: Function;
  toolTipArrow?: boolean;
  toolTipPosition?: string;
  isCtcAdmin?: boolean;
};

const getselected = (currentpage: string, page: string): boolean => {
  const splitCurrentPage = currentpage?.split('/').filter(token => !!token);
  if (splitCurrentPage?.length) {
    const base = `/${splitCurrentPage[0]}`;
    return !!(base?.includes(page));
  }
  return false;
};

type StylesProps = {
  fullHeight?: boolean;
  navBarMaxHeight?: string;
}

export const useStyles = makeStyles((theme) => ({
  listItem : {
    backgroundImage : `linear-gradient(-75deg, transparent 50%, ${dlsColor('caBlue')} 50%) !important`,
    height          : ({fullHeight}: StylesProps) => fullHeight ? '100%' : undefined
  },
  hoverlistItem : {
    '&:hover' : {
      backgroundImage : `linear-gradient(-75deg, transparent 50%, ${dlsColor('arcticBlue')} 50%) !important`,
      backgroundColor : `${dlsColor('arcticBlue')} !important`
    }
  },
  activeItem : {
    backgroundColor : `${dlsColor('caBlue')} !important`
  },
  navBar : {
    flex          : 1,
    minHeight     : 0,
    maxHeight     : ({navBarMaxHeight}: StylesProps) => navBarMaxHeight,
    flexDirection : 'column',
    display       : 'flex',
    overflowY     : 'auto',
    overflowX     : 'hidden'
  },
  '@keyframes arrowAnimation' : {
    '0%' : {
      transform : 'translateX(60%)'
    },
    '100%' : {
      transform : 'translateX(-60%)'
    }
  },
  leftArrow : {
    position                : 'fixed',
    width                   : 0,
    height                  : 0,
    borderTop               : '6px solid transparent',
    borderBottom            : '6px solid transparent',
    borderRight             : `6px solid ${dlsColor('grey1')}`,
    transform               : 'translateX(-60%)',
    animationName           : '$arrowAnimation',
    animationDuration       : '300ms',
    animationFillMode       : 'both',
    animationTimingFunction : 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    zIndex                  : dlsZIndex('contentLoaderContainer'),
    '&:before'              : {
      content         : '""',
      position        : 'absolute',
      top             : '-45px',
      left            : '-20px',
      width           : '45px',
      height          : '80px',
      backgroundColor : 'transparent',
      zIndex          : dlsZIndex('contentLoaderContainer')
    }
  },
  tooltip : {
    backgroundColor : `${dlsColor('arcticBlue')} !important`
  }
}));

// defines the styles used by tooltip
export const useStylesTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  '&::after' : {
    borderLeft : `8px solid ${dlsColor('caBlue')} !important`
  },
  [theme.breakpoints.down('md')] : {
    tooltip : {
      backgroundColor : dlsColor('grey1'),
      marginLeft      : '265px',
      padding         : '15px 8px'
    }
  },
  [theme.breakpoints.up('md')] : {
    tooltip : {
      ...dlsAlt('components', 'navTooltip')
    }
  }
}));

/**
 * @NavigationItem
 *
 * Defines the Navigation Item Component. This component is not stand-alone and is purely
 * meant to render the navigation items within Navigation component. Includes props for
 * any sideSheets which are related to each nav item.
 *
 * ```typescript
 *
 *   const navItems = [
 *     {icon: <Page1Icon>, label: t('page1:title), pages: '/pages1' },
 *     {icon: <Page2Icon>, label: t('page2:title), pages: '/pages2' },
 *   ];
 *
 *   <Navigation menuItems={navItems}
 *      onSetPage={onSetPage}
 *      selectedIndex={selectedIndex}
 *    >
 *      <Drawer>
 *        <NavigationItem menuItems={menuItems}
 *          selectedIndex={selectedIndex}
 *          onSetPage={function() {...}}
 *          sideSheetOrigin='right'
 *          toggleSideSheet={function() {...}}
            sideSheetStatus={{...}} />
 *      </Drawer>
 *   </Navigation>
 *
 * ```
 */
export const NavigationItem = ({
  menuItems,
  selectedIndex,
  activeIndex,
  hasMultipleSubModule,
  onSetPage,
  sideSheetOrigin = SideSheetOrigin.left,
  toggleSideSheet,
  sideSheetStatus,
  isInitialized,
  isLoaded,
  showSecondaryNavigation,
  closeSecondaryNavigation,
  secondaryNavOpen,
  currentPage,
  overrideStyle,
  isSideSheetArrowVisible,
  expandClasses,
  onHandleCollapse,
  collapsed = false,
  useExpandCollapseFeature = false,
  toolTipArrow = false,
  isCtcAdmin = false,
  fullHeight = false,
  toolTipPosition = 'bottom'
}: NavigationItemTypes) => {
  const {isDesktop}                    = useMedia();
  const navBarMaxHeight                = useGlobalProps('NavigationItem.navBar.maxHeight') as string;
  const classes                        = useStyles({fullHeight, navBarMaxHeight});
  const toolTipClassess                = useNavigationStyle();
  const [tooltipId, toggleToolTip]     = useToggleToolTip(-1);
  const [anchorEl, setAnchorEl]        = useState();
  const anchorRef                      = useRef(null);
  const secondaryNavRef                = useRef(null);
  const secondaryNavigationPositionRef = useRef<any>({});

  useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, []);

  const handleTooltipClose = useCallback(function() {
    toggleToolTip(-1);
  }, []);

  function CustomTooltip(props: any) {
    const index = props?.index;
    const title = translate('nav:underDevelopment.title');
    const message = translate('nav:underDevelopment.message');
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}
        mountListener>
        <Tooltip title={(<div>{title}<br />{message}</div>)}
          placement='left'
          {...props}
          open={tooltipId === index}
          arrow />
      </ClickAwayListener>
    );
  }

  function getClassNames(
    isOpenSecondaryMenu: boolean,
    activeItem: boolean,
    selected: boolean) {
    const isActive = selected && !isOpenSecondaryMenu;
    const styles = [
      navStyles[`${isActive ? 'active' : 'inactive'}${showSecondaryNavigation ? 'Nested' : ''}ListItem`],
      isActive ? classes.activeItem : null
    ];
    if (isOpenSecondaryMenu) {
      styles.push(navStyles.tooltip);
      styles.push(classes.tooltip);
    }
    if (activeItem && !showSecondaryNavigation) {
      styles.push(navStyles.hoverlistItem);
      styles.push(classes.hoverlistItem);
    }
    if (!showSecondaryNavigation) {
      styles.push(useExpandCollapseFeature ? expandClasses.navigationItem : navStyles.listItemBox);
      styles.push(navStyles.listItem);
      styles.push(classes.listItem);
      styles.push(classes.hoverlistItem);
    }
    return classnames([...styles]);
  }

  const onMouseMoveHandler = function(e) {
    if (isDesktop && !secondaryNavRef.current?.contains(e.target)) {
      const {top, bottom} = secondaryNavRef.current?.getBoundingClientRect() || {};
      const navWidth = dlsLayout(collapsed ? 'collapsedSidebar' : 'sidebar').width + 15;
      if (e.clientX > navWidth || e.clientY < top || e.clientY > bottom) {
        closeSecondaryNavigation?.();
      }
    }
  };

  return (
    <>
      <List className={showSecondaryNavigation
        ? navStyles.nestedList : navStyles.list}>
        <div className={classes.navBar}>
          {menuItems?.map((item, index) => {
            const {
              key,
              label,
              isActive,
              icon,
              meta,
              page,
              isPreview,
              showSideSheet
            } = item;
            const unifiedPages        = page.includes('maintenance') || page.includes('crashboxx') || page.includes('eld');
            const hasSecondaryPages   = !!item?.pages?.length;
            const isSecondaryPage     = (secondaryNavOpen || activeIndex === index) && hasSecondaryPages;
            const isOpenSecondaryMenu = isDesktop && (hasMultipleSubModule
              ? (isSecondaryPage && index === selectedIndex)
              : activeIndex === index && isSecondaryPage);
            const activeItem          = !showSecondaryNavigation ? isActive : isPreview;
            const tempNavigation      = !showSecondaryNavigation ? selectedIndex === index : page === currentPage;
            const selected            = !isCtcAdmin
              ? tempNavigation
              : getselected(currentPage, page) ||
                !!item?.pages?.some((child: GenericObject) => getselected(currentPage, child.page));
            const hoverEffectEnabled  = isDesktop && hasSecondaryPages;

            const onPageHandler = function(e, isMainMenu) {
              setAnchorEl(e.currentTarget);
              if (hasSecondaryPages) {
                // get the position of the position of the the container
                const {left, top, width, height} = e.currentTarget?.getBoundingClientRect() || {};
                const arrowLeft = left + width + 6.5;
                const arrowTop = top + height - (collapsed ? 25 : 35);

                secondaryNavigationPositionRef.current = {
                  arrowLeft, arrowTop
                };
              }
              activeItem
              // [DLS PACKAGE TODO]: Refactor to parameters object
                ? onSetPage(page, index, false, item, isMainMenu)
                : toggleToolTip(index);
            };

            const onMouseMove = function(e, isMainMenu) {
              if (index !== activeIndex || !isOpenSecondaryMenu) {
                if (hasSecondaryPages) {
                  onPageHandler(e, isMainMenu);
                }
              }
            };

            const onMouseLeave = function(e) {
              if (hoverEffectEnabled && isOpenSecondaryMenu) {
                closeSecondaryNavigation?.();
              }
              activeItem
                ? e.preventDefault()
                : isDesktop ? handleTooltipClose() : e.preventDefault();
            };

            const NavItem = (
              <ListItem button
                ref={anchorRef}
                key={key ?? label}
                disableTouchRipple
                className={getClassNames(isOpenSecondaryMenu, activeItem, selected)}
                selected={selected}
                onMouseLeave={onMouseLeave}
                onMouseMoveCapture={hoverEffectEnabled ? function(e) { onMouseMove(e, false); } : undefined}
                onClick={function(e) { (hoverEffectEnabled && !unifiedPages) ? undefined : onPageHandler(e, true); }}>
                <div className={navStyles.itemContentWrapper}>
                  {isOpenSecondaryMenu &&
                  <>
                    {isDesktop && (
                      <span className={classes.leftArrow} style={{
                        left : secondaryNavigationPositionRef.current.arrowLeft,
                        top  : secondaryNavigationPositionRef.current.arrowTop
                      }}/>
                    )}
                    <SecondaryNavigation onSetPage={onSetPage}
                      currentPage={currentPage}
                      nestedMenuItems={item?.pages}
                      anchorEl={anchorEl}
                      onMouseMove={onMouseMoveHandler}
                      isDesktop
                      createChildren={function(onSetPage, currentPage, nestedMenuItems = item?.pages) {
                        return (
                          <SecondaryNavigationItems onSetPage={onSetPage}
                            currentPage={currentPage}
                            secondaryNavRef={secondaryNavRef}
                            nestedMenuItems={nestedMenuItems} />
                        );
                      }} />
                  </>
                  }
                  {icon && !showSecondaryNavigation &&(
                    <ListItemIcon className={navStyles.listItemIcon}>
                      {icon && React.cloneElement(icon, {
                        color : !activeItem ? dlsColor('grey2') : selected ? dlsColor('grey1') : dlsColor('white')
                      })}
                    </ListItemIcon>
                  )}
                  {(!collapsed || !useExpandCollapseFeature) && (
                    <ListItemText className={
                      (secondaryNavOpen === null)
                        ? navStyles[`${(selected && showSecondaryNavigation) ? 'active' : 'inactive'}NestedListItemText`]
                        : ''
                    } classes={{
                      root      : navStyles[`${!activeItem ? 'disable' : ''}listItemText`],
                      primary   : navStyles[`${!showSecondaryNavigation ? '' : 'nested'}listItemTextPrimary`],
                      secondary : navStyles.itemSecondaryText
                    }} primary={<PrimaryText label={label} tag={meta?.tag} />}
                    secondary={translate(meta?.text)} />
                  )}
                </div>
                <Hidden hide='mobile'>
                  {isInitialized !== false && isLoaded !== false && isSideSheetArrowVisible &&
                  <SideSheetArrow sideSheetOrigin={sideSheetOrigin}
                    className={(showSideSheet && !index) ? overrideStyle?.sideSheetArrow : null}
                    showSideSheet={selected && showSideSheet}
                    onClick={
                      function() {
                        toggleSideSheet({
                          page,
                          desktopStatus : !sideSheetStatus.desktopSideSheetOpen
                        });
                      }
                    } sideSheetStatus={sideSheetStatus} />}
                </Hidden>
              </ListItem>
            );
            if (!isPreview) {
              return (
                <CustomTooltip key={label}
                  index={index}>
                  {NavItem}
                </CustomTooltip>
              );
            }
            if (!hoverEffectEnabled && useExpandCollapseFeature && collapsed) {
              return (
                <IonTooltip title={<span style={{marginLeft: '8px'}}>{label}</span>}
                  externalClasses={toolTipClassess}
                  placement={toolTipPosition as any}
                  arrow={toolTipArrow}>
                  {NavItem}
                </IonTooltip>
              );
            }
            return NavItem;
          })}
        </div>
        {useExpandCollapseFeature && (
          <ListItem button classes={{root: navStyles.collapseView}}>
            <ExpandCollapseToggle enabled={useExpandCollapseFeature}
              collapsed={collapsed}
              currentPage={currentPage}
              onClick={onHandleCollapse}
              toolTipPosition={toolTipPosition}
              toolTipArrow={toolTipArrow} />
          </ListItem>
        )}
      </List>
    </>
  );
};

export default NavigationItem;
